import type { ReactElement } from 'react';
import { Fragment } from 'react';
import { Divider, ListItemButton } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemPrimaryText,
} from '@noah-labs/fe-shared-ui-components';
import { ChevronDown } from 'lucide-react';
import type {
  MarkdownRemarkFrontmatterHeaderMenuHeaderLinks,
  Maybe,
} from '../../../generated/types';
import { NavigationItem } from '../menus/NavigationItem';

type PpDesktopMenuItem = {
  hasSublinks?: boolean;
  isSelected: boolean;
  menuItem: Maybe<MarkdownRemarkFrontmatterHeaderMenuHeaderLinks>;
  onEnter: (label: string) => void;
  onLeave: () => void;
};

export const StyledIcon = styled(ChevronDown)<{ isSelected: boolean }>(({ isSelected, theme }) => ({
  color: isSelected ? theme.palette.websiteBrand.primary : 'inherit',
  fontSize: theme.typography.navigationItem.fontSize,
  marginLeft: theme.spacing(1),
  transform: isSelected ? 'rotate(180deg)' : 'none',
  transition: 'transform 0.2s linear',
}));

export const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  padding: theme.spacing(0, 0, 0.5),
  ':hover': {
    backgroundColor: 'transparent',
    span: {
      color: theme.palette.text.secondary,
    },
  },
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(1, 0),
  },
})) as typeof ListItemButton;

export function DesktopMenuItem({
  hasSublinks,
  isSelected,
  menuItem,
  onEnter,
  onLeave,
}: PpDesktopMenuItem): ReactElement {
  const theme = useTheme();
  if (hasSublinks) {
    return (
      <ListItem sx={{ width: '100px' }} onMouseLeave={onLeave}>
        <List disablePadding>
          <ListItem>
            <StyledListItemButton
              onMouseOver={(): void => onEnter(menuItem?.headerLabel as string)}
            >
              <ListItemPrimaryText color="text.dark" variant="navigationItem">
                {menuItem?.headerLabel}
              </ListItemPrimaryText>
              <ListItemIcon>
                <StyledIcon isSelected={isSelected} size={12} />
              </ListItemIcon>
            </StyledListItemButton>
          </ListItem>
          {isSelected && (
            <Fragment>
              <Divider
                sx={{
                  borderColor: theme.palette.websiteBrand.primary,
                  marginBottom: 0.5,
                  paddingTop: 0.5,
                }}
              />
              {menuItem?.sublinks?.map((sublink) => {
                if (!sublink) {
                  return null;
                }
                return (
                  <ListItem key={sublink.sublinkLabel}>
                    <StyledListItemButton component={NavigationItem} href={sublink?.sublinkUrl}>
                      <ListItemPrimaryText color="text.dark" variant="navigationItem">
                        {sublink?.sublinkLabel}
                      </ListItemPrimaryText>
                    </StyledListItemButton>
                  </ListItem>
                );
              })}
            </Fragment>
          )}
        </List>
      </ListItem>
    );
  }
  return (
    <ListItem sx={{ width: '100px' }}>
      <StyledListItemButton component={NavigationItem} href={menuItem?.headerURL}>
        <ListItemPrimaryText color="text.dark" variant="navigationItem">
          {menuItem?.headerLabel}
        </ListItemPrimaryText>
      </StyledListItemButton>
    </ListItem>
  );
}
